import React from "react";
import { Check, Star } from "lucide-react";
import { SYMPTOMS, SYMPTOM_TO_PILLAR, PILLARS, pillarName, pillarColor } from "../../mock";

const LABEL = Object.fromEntries(SYMPTOMS.map((s) => [s.id, s.label]));

// "What You Told Us": one card per system she checked, START HERE first, then supporting, then wheel order.
export const WhatYouToldUs = ({ checkedItems, primarySymptom, startHere, supporting }) => {
  const checked = (checkedItems || []).filter((id) => SYMPTOM_TO_PILLAR[id]);
  const ordered = [startHere, ...(supporting || []), ...PILLARS.map((p) => p.id)].filter(
    (pid, i, arr) => pid && arr.indexOf(pid) === i
  );
  const cards = ordered
    .map((pid) => {
      const items = checked.filter((id) => SYMPTOM_TO_PILLAR[id] === pid);
      items.sort((a, b) => (a === primarySymptom ? -1 : b === primarySymptom ? 1 : 0));
      return { pid, items };
    })
    .filter((c) => c.items.length > 0);

  return (
    <section className="w-[90%] max-w-[760px] mx-auto mt-20" data-testid="what-you-told-us">
      <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl text-white text-center">
        What You Told Us
      </h2>
      <p className="mt-4 text-lg text-white/75 font-medium leading-relaxed text-center">
        Here's everything you checked, sorted by the system it comes from.
      </p>
      <div className="mt-8 space-y-4">
        {cards.map(({ pid, items }) => {
          const tag = pid === startHere ? "Start Here" : (supporting || []).includes(pid) ? "Supporting" : null;
          return (
            <div
              key={pid}
              className="rounded-[1.5rem] bg-[#111111] border border-[#2A2A2A] p-5 md:p-6"
              data-testid={`told-card-${pid}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <span className="w-3 h-3 mt-1.5 rounded-full shrink-0" style={{ backgroundColor: pillarColor(pid) }} aria-hidden="true" />
                  <h3 className="font-display font-bold uppercase tracking-tight text-lg text-white leading-tight">{pillarName(pid)}</h3>
                </div>
                {tag && (
                  <span className="shrink-0 text-[10px] font-bold tracking-[0.2em] uppercase text-white/55 bg-white/[0.06] border border-white/10 rounded-full px-2.5 py-1">
                    {tag}
                  </span>
                )}
              </div>
              <ul className="mt-4 space-y-2.5">
                {items.map((id) => {
                  const isTop = id === primarySymptom;
                  return (
                    <li key={id} className="flex items-start gap-3 text-base text-white/80 font-medium leading-snug" data-testid={`told-item-${id}`}>
                      {isTop ? (
                        <Star size={18} className="mt-0.5 shrink-0 text-[#D4AF35] fill-[#D4AF35]" aria-label="Your number one" />
                      ) : (
                        <Check size={18} className="mt-0.5 shrink-0 text-white/60" aria-hidden="true" />
                      )}
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className={isTop ? "text-white" : ""}>{LABEL[id] || id}</span>
                        {isTop && (
                          <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#0D0D0D] bg-[#D4AF35] rounded-full px-2 py-0.5" data-testid="your-number-one-badge">
                            Your #1
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="mt-8 text-white font-semibold text-lg text-center leading-relaxed">
        None of these are random. Every one of them has a reason.
      </p>
    </section>
  );
};
